import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Plus, Download, CheckCircle2, XCircle, Clock, FileX } from 'lucide-react'

type ImportState = 'uploaded' | 'previewing' | 'previewed' | 'committing' | 'confirmed' | 'failed' | 'expired'

interface ImportRow {
  id: number
  data_source: string
  state: ImportState
  uploaded_at: string | null
  uploaded_by_name: string | null
  invalid_count: number
  has_invalid_rows: boolean
  counts: { total_rows: number; valid_count: number } | null
  row_counts: { inserted: number; updated: number } | null
}

interface PagyMeta {
  count: number
  page: number
  pages: number
  next: number | null
  prev: number | null
}

interface IndexProps {
  imports: ImportRow[]
  pagy: PagyMeta
  state_filter: string | null
}

const STATE_BADGE: Record<ImportState, { label: string; icon: typeof CheckCircle2; tone: string }> = {
  uploaded:   { label: 'Téléversé',       icon: Clock,        tone: 'text-muted-foreground bg-muted/50' },
  previewing: { label: 'Analyse',         icon: Clock,        tone: 'text-blue-700 bg-blue-50' },
  previewed:  { label: 'Aperçu prêt',     icon: CheckCircle2, tone: 'text-blue-700 bg-blue-50' },
  committing: { label: 'Import en cours', icon: Clock,        tone: 'text-amber-700 bg-amber-50' },
  confirmed:  { label: 'Terminé',         icon: CheckCircle2, tone: 'text-emerald-700 bg-emerald-50' },
  failed:     { label: 'Échec',           icon: XCircle,      tone: 'text-destructive bg-destructive/10' },
  expired:    { label: 'Expiré',          icon: FileX,        tone: 'text-muted-foreground bg-muted/50' },
}

const STATE_FILTERS: { label: string; value: string }[] = [
  { label: 'Tous',         value: '' },
  { label: 'En cours',     value: 'previewing' },
  { label: 'Aperçu prêt',  value: 'previewed' },
  { label: 'Import',       value: 'committing' },
  { label: 'Terminé',      value: 'confirmed' },
  { label: 'Échec',        value: 'failed' },
]

export default function OrganizationDonorImportsIndex({ imports, pagy, state_filter }: IndexProps) {
  function setFilter(value: string) {
    router.get(
      '/people/imports/organization_donors',
      value ? { state: value } : {},
      { preserveScroll: true, preserveState: true }
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Imports d'organisations donatrices</h1>
          <p className="text-sm text-muted-foreground">
            Historique des téléversements XLSX. {pagy.count} import{pagy.count > 1 ? 's' : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/people/imports/organization_donors/template"
            className="inline-flex items-center gap-1 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted"
          >
            <Download className="h-4 w-4" />
            Modèle
          </a>
          <Button asChild>
            <Link href="/people/imports/organization_donors/new">
              <Plus className="mr-1 h-4 w-4" />
              Nouvel import
            </Link>
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {STATE_FILTERS.map((f) => {
          const active = (state_filter || '') === f.value
          return (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                active ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {imports.length === 0 ? (
        <div className="rounded-md border bg-card p-10 text-center">
          <p className="text-muted-foreground">Aucun import à afficher.</p>
          <Button asChild className="mt-4">
            <Link href="/people/imports/organization_donors/new">Démarrer un import</Link>
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">État</th>
                <th className="px-3 py-2 text-right">Lignes</th>
                <th className="px-3 py-2 text-right">Insérées</th>
                <th className="px-3 py-2 text-right">M.à.j.</th>
                <th className="px-3 py-2 text-right">Invalides</th>
                <th className="px-3 py-2">Téléversé</th>
                <th className="px-3 py-2">Par</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => {
                const meta = STATE_BADGE[imp.state]
                const Icon = meta.icon
                return (
                  <tr key={imp.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link href={`/people/imports/organization_donors/${imp.id}`} className="text-primary hover:underline">
                        #{imp.id}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{imp.data_source}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${meta.tone}`}>
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{imp.counts?.total_rows ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{imp.row_counts?.inserted ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{imp.row_counts?.updated ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{imp.invalid_count || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {imp.uploaded_at ? new Date(imp.uploaded_at).toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{imp.uploaded_by_name ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagy.pages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Page {pagy.page} sur {pagy.pages}</span>
          <div className="flex gap-2">
            {pagy.prev && (
              <button
                type="button"
                onClick={() => router.get('/people/imports/organization_donors', { page: pagy.prev, state: state_filter })}
                className="rounded border px-2 py-1 hover:bg-muted"
              >
                Précédent
              </button>
            )}
            {pagy.next && (
              <button
                type="button"
                onClick={() => router.get('/people/imports/organization_donors', { page: pagy.next, state: state_filter })}
                className="rounded border px-2 py-1 hover:bg-muted"
              >
                Suivant
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
